import React, { useEffect } from 'react';
import { infantBaptisms as api } from '../../api/endpoints';
import { formatDate } from '../../utils/format';
import { FormLayout, Input, Select, FamilyPicker } from '../../components/ui';
import { useChurchConfig } from '../../hooks';
import useStyles from '../../theme/useStyles';
import type { RegisterConfig, FieldContext } from './registerConfig';
import RegisterMemberField from './MemberField';
import type { InfantBaptism, Member, Family } from '../../types';

// Baptism is the only register that is also an entry point.
//
// A newborn is not on the rolls — the baptism is usually the first time the
// church writes the child's name down at all. So the form opens the member
// record itself rather than sending the clerk to the member screen and back,
// which is the double entry this whole change exists to remove.
//
// Three modes, because all three happen:
//   NEW_CHILD  a child with no member record yet     (the common one)
//   MEMBER     an adult baptism, or a child already on the rolls
//   OUTSIDER   a visitor — recorded by name, no member touched

type Mode = 'NEW_CHILD' | 'MEMBER' | 'OUTSIDER';

const MODES: Array<{ key: Mode; label: string; hint: string }> = [
  { key: 'NEW_CHILD', label: '🆕 New child', hint: 'Not on the rolls yet — the member record is created with this entry.' },
  { key: 'MEMBER', label: '👤 Existing member', hint: 'An adult baptism, or a child already in the directory.' },
  { key: 'OUTSIDER', label: '🚶 Not a member', hint: 'A visitor. Recorded by name only; no member record is touched.' }
];

const GENDERS = ['Male', 'Female'];

const EMPTY = {
  memberId: '', childName: '', dob: '', baptismDate: '', parentsNames: '', godparents: '',
  minister: '', church: '', registerNo: '', certificateNo: '', remarks: '',
  // Sent only in NEW_CHILD mode; the backend opens the member from these.
  // `createMember` starts true because the blank form starts in that mode —
  // the two must agree, or a new child saves with no member record.
  createMember: true, newMemberFamilyId: '', newMemberGender: '', newMemberRelation: '',
  /** Form-local, stripped before saving. */
  _mode: 'NEW_CHILD' as Mode,
  _member: null as Member | null,
  _family: null as Family | null
};

type Form = typeof EMPTY;

const Certificate = (r: InfantBaptism) => (
  <div>
    <p>
      This is to certify that <strong>{r.childName}</strong>, born on{' '}
      <strong>{formatDate(r.dob)}</strong>, child of <strong>{r.parentsNames || '—'}</strong>,
      was baptised in the name of the Father, and of the Son, and of the Holy Spirit
      on <strong>{formatDate(r.baptismDate)}</strong> at <strong>{r.church || '—'}</strong>.
    </p>

    <div style={{ marginTop: '20px' }}>
      {r.memberId && <div><strong>Member ID:</strong> {r.memberId}</div>}
      <div><strong>Godparents / Sponsors:</strong> {r.godparents || '—'}</div>
      <div><strong>Officiating Minister:</strong> {r.minister || '—'}</div>
      {r.remarks && <div><strong>Remarks:</strong> {r.remarks}</div>}
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '70px' }}>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Minister</div>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Church Seal</div>
    </div>
  </div>
);

/** Everything the mode owns, cleared together so no stale link survives a switch. */
const applyMode = (form: Form, mode: Mode): Form => ({
  ...form,
  _mode: mode,
  _member: null,
  _family: mode === 'NEW_CHILD' ? form._family : null,
  memberId: '',
  createMember: mode === 'NEW_CHILD',
  newMemberFamilyId: mode === 'NEW_CHILD' ? form.newMemberFamilyId : '',
  newMemberGender: mode === 'NEW_CHILD' ? form.newMemberGender : '',
  newMemberRelation: ''
});

const BaptismFields = ({
  form, setForm, ctx
}: { form: Form; setForm: (next: Form) => void; ctx: FieldContext }) => {
  const s = useStyles(ctx.isDarkMode);
  const t = s.t;
  const config = useChurchConfig();

  // Nearly every baptism happens in this church, so the field starts filled in
  // and the clerk only edits it for the exception.
  useEffect(() => {
    if (!ctx.isEditing && !form.church && config.churchName) {
      setForm({ ...form, church: config.churchName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.churchName, ctx.isEditing]);

  // The mode and the create-member flag have to agree, or a child is filed with
  // no member record — or worse, a second one is opened for a member who
  // already exists. The mode is the single source of truth; this keeps the flag
  // behind it, including when the form arrives pre-seeded from another screen.
  const shouldCreate = form._mode === 'NEW_CHILD' && !ctx.isEditing;
  useEffect(() => {
    if (form.createMember !== shouldCreate) setForm({ ...form, createMember: shouldCreate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreate, form.createMember]);

  // An entry already on file has its member (or its absence) settled — offering
  // to create another one from an edit would duplicate the child.
  const modes = ctx.isEditing ? MODES.filter((m) => m.key !== 'NEW_CHILD') : MODES;
  const active = MODES.find((m) => m.key === form._mode) || MODES[0];

  return (
    <>
      <FormLayout.Section title="Who is being baptised?" />
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
        {modes.map((mode) => {
          const on = form._mode === mode.key;
          return (
            <button
              key={mode.key}
              type="button"
              onClick={() => setForm(applyMode(form, mode.key))}
              style={{
                padding: '9px 16px',
                border: `1px solid ${on ? t.primary : t.border}`,
                borderRadius: t.radius.md,
                cursor: 'pointer',
                fontWeight: on ? 'bold' : 'normal',
                fontSize: t.fontSize.md,
                backgroundColor: on ? t.primary : t.bgRaised,
                color: on ? '#FFF' : t.textMain
              }}
            >
              {mode.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginBottom: t.spacing.lg }}>
        {active.hint}
      </div>

      <FormLayout.Grid>
        {form._mode === 'NEW_CHILD' && (
          <>
            <FamilyPicker
              label="Family"
              value={form.newMemberFamilyId}
              required
              fullWidth
              isDarkMode={ctx.isDarkMode}
              hint="Pick the household by its head — the child is added under it."
              onChange={(familyId, family) => setForm({
                ...form,
                newMemberFamilyId: familyId,
                _family: family,
                // The register asks for both parents on one line; the head is
                // what the parish holds, so it is offered and left editable.
                parentsNames: family?.headOfFamily || form.parentsNames
              })}
            />
            <Select
              label="Gender" required options={GENDERS} placeholder="Select"
              value={form.newMemberGender}
              onChange={(e) => setForm({ ...form, newMemberGender: e.target.value })}
            />
          </>
        )}

        {form._mode === 'MEMBER' && (
          <RegisterMemberField
            form={form}
            setForm={setForm as (next: any) => void}
            idKey="memberId"
            memberKey="_member"
            label="Member Being Baptised"
            hint="Their name and date of birth fill in from the directory."
            required
            fullWidth
            isDarkMode={ctx.isDarkMode}
            autofill={(member) => ({
              childName: member?.name || '',
              dob: member?.dateOfBirth || '',
              parentsNames: member?.relativeName || ''
            })}
          />
        )}

        <Input
          label={form._mode === 'MEMBER' ? 'Full Name' : "Child's Full Name"}
          required placeholder="e.g. Grace Anna"
          value={form.childName} onChange={(e) => setForm({ ...form, childName: e.target.value })}
        />
        <Input
          label="Date of Birth" type="date"
          value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
        />
        <Input
          label="Date of Baptism" type="date" required
          value={form.baptismDate} onChange={(e) => setForm({ ...form, baptismDate: e.target.value })}
        />
        <Input
          label="Church" placeholder="Where the baptism took place"
          value={form.church} onChange={(e) => setForm({ ...form, church: e.target.value })}
        />
        <Input
          label="Parents' Names" required fullWidth placeholder="e.g. John & Mary Mathew"
          value={form.parentsNames} onChange={(e) => setForm({ ...form, parentsNames: e.target.value })}
        />
        <Input
          label="Godparents / Sponsors" fullWidth placeholder="Names of Godparents"
          value={form.godparents} onChange={(e) => setForm({ ...form, godparents: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Register Details" />
      <FormLayout.Grid>
        <Input
          label="Officiating Minister" placeholder="e.g. Rev. David Thomas"
          value={form.minister} onChange={(e) => setForm({ ...form, minister: e.target.value })}
        />
        <Input
          label="Baptism Register Number" placeholder="e.g. Vol 4, Page 22"
          value={form.registerNo} onChange={(e) => setForm({ ...form, registerNo: e.target.value })}
        />
        <Input
          label="Certificate Number" placeholder="e.g. BC-2026-014"
          value={form.certificateNo} onChange={(e) => setForm({ ...form, certificateNo: e.target.value })}
        />
        <Input
          label="Remarks" type="textarea" rows={2} fullWidth
          placeholder="Anything noted in the register..."
          value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
        />
      </FormLayout.Grid>
    </>
  );
};

const config: RegisterConfig<InfantBaptism, Form> = {
  icon: '💧',
  title: 'Child Baptism Register',
  prefix: 'IBP',
  label: 'Baptism Entry',
  resource: api,
  emptyForm: EMPTY,
  toForm: (r) => ({
    ...EMPTY,
    // An entry on file never re-creates a member: it either already names one
    // or it never did.
    _mode: (r.memberId ? 'MEMBER' : 'OUTSIDER') as Mode,
    createMember: false,
    memberId: r.memberId || '',
    childName: r.childName || '', dob: r.dob || '', baptismDate: r.baptismDate || '',
    parentsNames: r.parentsNames || '', godparents: r.godparents || '',
    minister: r.minister || '', church: r.church || '',
    registerNo: r.registerNo || '', certificateNo: r.certificateNo || '', remarks: r.remarks || ''
  }),
  validate: (f) => {
    if (f._mode === 'NEW_CHILD') {
      if (!f.newMemberFamilyId) return 'Choose the family the child belongs to.';
      if (!f.newMemberGender) return "Select the child's gender.";
    }
    if (f._mode === 'MEMBER' && !f.memberId) return 'Choose the member being baptised.';
    if (!f.childName.trim()) return "The child's name is required.";
    if (!f.baptismDate) return 'The date of baptism is required.';
    // A baptism register entry without the parents named is incomplete.
    if (!f.parentsNames.trim()) return "The parents' names are required.";
    if (f.dob && f.baptismDate && f.dob > f.baptismDate) {
      return 'The baptism cannot be dated before the child was born.';
    }
    return null;
  },
  warn: (f) => {
    const m = f._member;
    if (m?.baptismDate && m.baptismDate !== f.baptismDate) {
      return `${m.name || m.memberId} is already recorded as baptised on ${formatDate(m.baptismDate)}. `
        + 'Saving will replace that date on the member record.';
    }
    return null;
  },
  rowDate: (r) => r.baptismDate,
  searchKeys: ['recordId', 'memberId', 'childName', 'parentsNames', 'godparents', 'registerNo', 'certificateNo', 'minister'],
  searchPlaceholder: 'Search by Child, Parents, Sponsors or Register No...',
  columns: [
    {
      key: 'recordId',
      header: 'Date & Reg No.',
      nowrap: true,
      render: (r: InfantBaptism) => (
        <>
          <strong>{formatDate(r.baptismDate)}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.registerNo || r.recordId}</div>
        </>
      )
    },
    {
      key: 'childName',
      header: "Child's Name & DOB",
      render: (r: InfantBaptism) => (
        <>
          <strong>{r.childName}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>
            {[formatDate(r.dob), r.memberId].filter((v) => v && v !== '-').join('  ·  ')}
          </div>
        </>
      )
    },
    {
      key: 'parentsNames',
      header: 'Parents & Sponsors',
      render: (r: InfantBaptism) => (
        <>
          <div>{r.parentsNames || '-'}</div>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.godparents || '-'}</div>
        </>
      )
    },
    {
      key: 'minister',
      header: 'Minister & Church',
      render: (r: InfantBaptism) => (
        <>
          <div>{r.minister || '-'}</div>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.church || '-'}</div>
        </>
      )
    },
    { key: 'certificateNo', header: 'Cert No.', render: (r: InfantBaptism) => r.certificateNo || '-' }
  ],
  printModule: 'Baptism',
  printDocumentType: 'BAPTISM_CERTIFICATE',
  listDocumentType: 'BAPTISM_REGISTER',
  printData: (r: any) => ({
    registerNo: r.registerNo, certificateNo: r.certificateNo,
    childName: r.childName, dob: formatDate(r.dob), baptismDate: formatDate(r.baptismDate),
    parentsNames: r.parentsNames, godparents: r.godparents,
    minister: r.minister, church: r.church, remarks: r.remarks,
    memberId: r.memberId, recordId: r.recordId
  }),
  renderDocument: Certificate,
  fields: (form, setForm, ctx) => (
    <BaptismFields form={form} setForm={setForm as (next: Form) => void} ctx={ctx} />
  )
};

export { config as childBaptismConfig };
